// src/components/ElementCanvas.tsx
import React, { useRef, useEffect } from "react";
import { ElementDef } from "../store/elementStore";

interface ElementCanvasProps {
  e: ElementDef;
  size?: number;
}

export default function ElementCanvas({ e, size = 64 }: ElementCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frame = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // ←–– ADDED GUARD
    if (!e) {
      // you could even console.warn here
      return;
    }

    // Assert non-null so TS knows this is always a CanvasRenderingContext2D
    const ctx = canvas.getContext("2d")!;

    let animationId: number;
    const w = size,
      h = size;

    function draw() {
      frame.current++;
      ctx.clearRect(0, 0, w, h);

      // Circular clipping
      ctx.save();
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, w / 2, 0, Math.PI * 2);
      ctx.clip();

      // Get animation type from element or fall back to a default
      const animationType = e.animation || "default";

      switch (animationType) {
        // Original animations
        case "waterWave":
          {
            // Water radial gradient
            const grad = ctx.createRadialGradient(
              w / 2,
              h / 2,
              w * 0.1,
              w / 2,
              h / 2,
              w / 1.2
            );
            grad.addColorStop(0, "#64B5F6");
            grad.addColorStop(1, e.color || "#2196F3");
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, w, h);

            // Animated waves
            ctx.strokeStyle = "rgba(255,255,255,0.5)";
            ctx.lineWidth = 2;
            const speed = frame.current * 0.02;
            for (let y = h * 0.6; y < h + 10; y += 12) {
              ctx.beginPath();
              ctx.moveTo(0, y + Math.sin(speed + y * 0.1) * 4);
              ctx.quadraticCurveTo(
                w / 4,
                y + Math.cos(speed + y * 0.15) * 4,
                w / 2,
                y + Math.sin(speed + y * 0.1) * 4
              );
              ctx.quadraticCurveTo(
                (3 * w) / 4,
                y - Math.cos(speed + y * 0.15) * 4,
                w,
                y + Math.sin(speed + y * 0.1) * 4
              );
              ctx.stroke();
            }
          }
          break;

        case "fireFlicker":
          {
            // Flickering gradient
            const spread = Math.sin(frame.current * 0.1) * 0.3 + 0.7;
            const grad = ctx.createRadialGradient(
              w / 2,
              h * 0.7,
              w * 0.1,
              w / 2,
              h / 2,
              (w / 1.1) * spread
            );
            grad.addColorStop(0, "#FFE082");
            grad.addColorStop(0.5, e.color || "#FF5722");
            grad.addColorStop(1, "rgba(255,87,34,0)");
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, w, h);

            // Flickering flame shape
            ctx.fillStyle = "rgba(255,235,59,0.6)";
            ctx.beginPath();
            const sway = Math.sin(frame.current * 0.15) * 4;
            ctx.moveTo(w / 2 + sway, h * 0.2);
            ctx.bezierCurveTo(
              w * 0.3,
              h * 0.5,
              w * 0.7,
              h * 0.5,
              w / 2 - sway,
              h * 0.8
            );
            ctx.bezierCurveTo(
              w * 0.2,
              h * 0.6,
              w * 0.8,
              h * 0.6,
              w / 2 + sway,
              h * 0.2
            );
            ctx.fill();
          }
          break;

        case "airSwirl":
          {
            // Pale background
            ctx.fillStyle = e.color || "#ECEFF1";
            ctx.fillRect(0, 0, w, h);

            // Rotating swirls
            ctx.strokeStyle = "rgba(144,164,174,0.6)";
            ctx.lineWidth = 1.5;
            const angle = frame.current * 0.02;
            for (let i = 0; i < 3; i++) {
              ctx.save();
              ctx.translate(w / 2, h / 2);
              ctx.rotate(angle + (i * Math.PI) / 3);
              ctx.beginPath();
              ctx.arc(0, 0, w * 0.3 + i * 5, -Math.PI * 0.8, Math.PI * 0.2);
              ctx.stroke();
              ctx.restore();
            }
          }
          break;

        // New animations based on element types
        case "earthCrackle":
          {
            // Earth texture
            ctx.fillStyle = e.color || "#8B4513";
            ctx.fillRect(0, 0, w, h);

            // Crackling effect
            ctx.strokeStyle = "rgba(0,0,0,0.3)";
            ctx.lineWidth = 1;
            const time = frame.current * 0.01;

            for (let i = 0; i < 4; i++) {
              ctx.beginPath();
              const startX = w * 0.2 + Math.sin(time + i) * 10;
              const startY = h * 0.2 + Math.cos(time + i) * 10;
              ctx.moveTo(startX, startY);

              let x = startX,
                y = startY;
              for (let j = 0; j < 5; j++) {
                x += Math.sin(time + i + j) * 10;
                y += Math.cos(time + i + j * 0.5) * 5 + 6;
                ctx.lineTo(x, y);
              }
              ctx.stroke();
            }
          }
          break;

        case "aetherGlow":
          {
            // Aether pulsing glow
            const pulseSize = Math.sin(frame.current * 0.05) * 0.2 + 0.8;
            const grad = ctx.createRadialGradient(
              w / 2,
              h / 2,
              w * 0.1,
              w / 2,
              h / 2,
              (w / 1.5) * pulseSize
            );
            grad.addColorStop(0, "#E1BEE7");
            grad.addColorStop(0.6, e.color || "#9C27B0");
            grad.addColorStop(1, "rgba(156,39,176,0)");
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, w, h);

            // Floating particles
            ctx.fillStyle = "rgba(255,255,255,0.8)";
            const time = frame.current * 0.05;
            for (let i = 0; i < 12; i++) {
              const angle = (i / 12) * Math.PI * 2 + time;
              const dist = (Math.sin(time + i) * 0.2 + 0.8) * w * 0.35;
              const x = w / 2 + Math.cos(angle) * dist;
              const y = h / 2 + Math.sin(angle) * dist;
              const size = Math.max(0.5, Math.sin(time + i * 0.7) * 2 + 3);
              ctx.beginPath();
              ctx.arc(x, y, size, 0, Math.PI * 2);
              ctx.fill();
            }
          }
          break;

        case "metalShine":
          {
            // Metal base
            ctx.fillStyle = e.color || "#607D8B";
            ctx.fillRect(0, 0, w, h);

            // Shiny highlight
            ctx.fillStyle = "rgba(255,255,255,0.5)";
            const time = frame.current * 0.03;
            ctx.beginPath();
            ctx.ellipse(
              w / 2 + Math.sin(time) * w * 0.2,
              h / 2 + Math.cos(time) * h * 0.2,
              w * 0.15,
              h * 0.05,
              time,
              0,
              Math.PI * 2
            );
            ctx.fill();

            // Additional small reflections
            ctx.fillStyle = "rgba(255,255,255,0.3)";
            for (let i = 0; i < 3; i++) {
              ctx.beginPath();
              ctx.arc(
                w * 0.3 + i * w * 0.2,
                h * 0.3 + i * h * 0.15,
                2 + i,
                0,
                Math.PI * 2
              );
              ctx.fill();
            }
          }
          break;

        case "steamRise":
          {
            // Steam background
            ctx.fillStyle = e.color || "#B3E5FC";
            ctx.fillRect(0, 0, w, h);

            // Rising steam puffs
            ctx.fillStyle = "rgba(255,255,255,0.7)";
            const time = frame.current * 0.05;

            for (let i = 0; i < 5; i++) {
              const x = w * 0.2 + i * w * 0.15 + Math.sin(time + i) * 5;
              const baseY = h - (time % 1) * h;
              const y = baseY + i * 10;
              const size = Math.max(1, 10 + Math.sin(time + i) * 3);

              if (y > 0 && y < h + size) {
                ctx.beginPath();
                ctx.arc(x, y, size, 0, Math.PI * 2);
                ctx.fill();
              }
            }
          }
          break;

        case "lavaFlow":
          {
            // Lava base
            ctx.fillStyle = e.color || "#FF7043";
            ctx.fillRect(0, 0, w, h);

            // Flowing lava effect
            const time = frame.current * 0.02;
            for (let i = 0; i < 4; i++) {
              // Create flowing pattern
              ctx.fillStyle = i % 2 === 0 ? "#FF5722" : "#FFAB91";
              ctx.beginPath();

              const yOffset = ((time + i * 0.25) % 1) * h;
              const amplitude = 10;

              ctx.moveTo(0, yOffset);
              for (let x = 0; x < w; x += 10) {
                const y = yOffset + Math.sin(x * 0.1 + time) * amplitude;
                ctx.lineTo(x, y);
              }
              ctx.lineTo(w, yOffset);
              ctx.lineTo(w, yOffset + 15);
              ctx.lineTo(0, yOffset + 15);
              ctx.fill();
            }

            // Bubbling effect
            ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
            for (let i = 0; i < 5; i++) {
              const x = w * 0.2 + i * w * 0.15;
              const y = h * 0.3 + Math.sin(time + i) * 10;
              const size = Math.max(0.5, 3 + Math.sin(time * 2 + i) * 2);

              ctx.beginPath();
              ctx.arc(x, y, size, 0, Math.PI * 2);
              ctx.fill();
            }
          }
          break;

        case "lightPulse":
          {
            // Light pulsing effect
            const pulseSize = Math.sin(frame.current * 0.08) * 0.2 + 0.8;
            const grad = ctx.createRadialGradient(
              w / 2,
              h / 2,
              0,
              w / 2,
              h / 2,
              (w / 1.2) * pulseSize
            );
            grad.addColorStop(0, "#FFFFFF");
            grad.addColorStop(0.5, e.color || "#FFEB3B");
            grad.addColorStop(1, "rgba(255,235,59,0.3)");
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, w, h);

            // Rays of light
            ctx.strokeStyle = "rgba(255,255,255,0.7)";
            ctx.lineWidth = 1;
            const time = frame.current * 0.02;

            for (let i = 0; i < 8; i++) {
              const angle = (i / 8) * Math.PI * 2 + time;
              ctx.beginPath();
              ctx.moveTo(w / 2, h / 2);
              ctx.lineTo(
                w / 2 + Math.cos(angle) * w,
                h / 2 + Math.sin(angle) * h
              );
              ctx.stroke();
            }
          }
          break;

        case "darkSwirl":
          {
            // Dark base
            ctx.fillStyle = e.color || "#424242";
            ctx.fillRect(0, 0, w, h);

            // Swirling darkness
            const time = frame.current * 0.02;
            for (let i = 0; i < 3; i++) {
              ctx.beginPath();
              ctx.arc(
                w / 2,
                h / 2,
                Math.max(1, w * 0.4 - i * 10),
                time + i,
                time + Math.PI + i
              );
              ctx.strokeStyle = `rgba(0,0,0,${0.3 + i * 0.2})`;
              ctx.lineWidth = 5;
              ctx.stroke();
            }

            // Subtle sparkles
            ctx.fillStyle = "rgba(100,100,100,0.3)";
            for (let i = 0; i < 10; i++) {
              const angle = (i / 10) * Math.PI * 2 + time;
              const dist = Math.random() * w * 0.4;
              ctx.beginPath();
              ctx.arc(
                w / 2 + Math.cos(angle) * dist,
                h / 2 + Math.sin(angle) * dist,
                1,
                0,
                Math.PI * 2
              );
              ctx.fill();
            }
          }
          break;

        case "mudDrip":
          {
            // Mud base
            ctx.fillStyle = e.color || "#8D6E63";
            ctx.fillRect(0, 0, w, h);

            // Dripping effect
            ctx.fillStyle = "rgba(121,85,72,0.6)";
            const time = frame.current * 0.03;

            for (let i = 0; i < 3; i++) {
              const x = w * 0.25 + i * w * 0.25;
              const length = 15 + Math.sin(time + i) * 5;
              const startY = h * 0.3 + i * 10;

              // Drip
              ctx.beginPath();
              ctx.moveTo(x, startY);
              ctx.lineTo(x + 5, startY);
              ctx.lineTo(x + 2.5, startY + length);
              ctx.fill();

              // Drip droplet
              if (Math.sin(time * 2 + i) > 0.7) {
                ctx.beginPath();
                ctx.arc(x + 2.5, startY + length + 5, 3, 0, Math.PI * 2);
                ctx.fill();
              }
            }
          }
          break;

        case "lifePulse":
          {
            // Life base with pulsing
            const pulseSize = Math.sin(frame.current * 0.05) * 0.15 + 0.85;
            const grad = ctx.createRadialGradient(
              w / 2,
              h / 2,
              w * 0.1,
              w / 2,
              h / 2,
              (w / 1.8) * pulseSize
            );
            grad.addColorStop(0, "#C5E1A5");
            grad.addColorStop(0.7, e.color || "#8BC34A");
            grad.addColorStop(1, "rgba(139,195,74,0.3)");
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, w, h);

            // Growing spiral pattern
            ctx.strokeStyle = "rgba(255,255,255,0.6)";
            ctx.lineWidth = 2;
            const time = frame.current * 0.01;

            ctx.beginPath();
            for (let i = 0; i < 100; i++) {
              const t = i * 0.1;
              const x = w / 2 + Math.cos(t + time) * t * 0.3;
              const y = h / 2 + Math.sin(t + time) * t * 0.3;

              if (i === 0) {
                ctx.moveTo(x, y);
              } else {
                ctx.lineTo(x, y);
              }

              if (t > w / 2) break;
            }
            ctx.stroke();
          }
          break;

        case "iceCrystal":
          {
            // Ice base
            ctx.fillStyle = e.color || "#80DEEA";
            ctx.fillRect(0, 0, w, h);

            // Crystal patterns
            ctx.strokeStyle = "rgba(255,255,255,0.7)";
            ctx.lineWidth = 1;
            const time = frame.current * 0.01;

            // Crystalline structure
            for (let i = 0; i < 6; i++) {
              const angle = (i / 6) * Math.PI * 2 + time;
              ctx.beginPath();
              ctx.moveTo(w / 2, h / 2);

              const endX = w / 2 + Math.cos(angle) * w * 0.4;
              const endY = h / 2 + Math.sin(angle) * h * 0.4;
              ctx.lineTo(endX, endY);

              // Add branches
              const branchAngle1 = angle + Math.PI / 6;
              const branchAngle2 = angle - Math.PI / 6;

              ctx.moveTo(
                w / 2 + Math.cos(angle) * w * 0.2,
                h / 2 + Math.sin(angle) * h * 0.2
              );
              ctx.lineTo(
                w / 2 +
                  Math.cos(angle) * w * 0.2 +
                  Math.cos(branchAngle1) * w * 0.15,
                h / 2 +
                  Math.sin(angle) * h * 0.2 +
                  Math.sin(branchAngle1) * h * 0.15
              );

              ctx.moveTo(
                w / 2 + Math.cos(angle) * w * 0.2,
                h / 2 + Math.sin(angle) * h * 0.2
              );
              ctx.lineTo(
                w / 2 +
                  Math.cos(angle) * w * 0.2 +
                  Math.cos(branchAngle2) * w * 0.15,
                h / 2 +
                  Math.sin(angle) * h * 0.2 +
                  Math.sin(branchAngle2) * h * 0.15
              );

              ctx.stroke();
            }

            // Shimmer effect - THIS IS WHERE THE ERROR WAS HAPPENING
            ctx.fillStyle = "rgba(255,255,255,0.6)";
            for (let i = 0; i < 10; i++) {
              const x = Math.random() * w;
              const y = Math.random() * h;
              // Ensure size is always positive
              const size = Math.max(0.5, Math.sin(time * 5 + i) * 1.5 + 1);

              ctx.beginPath();
              ctx.arc(x, y, size, 0, Math.PI * 2);
              ctx.fill();
            }
          }
          break;

        case "stormCloud":
          {
            // Storm cloud base
            ctx.fillStyle = e.color || "#7986CB";
            ctx.fillRect(0, 0, w, h);

            // Cloud shapes
            ctx.fillStyle = "#546E7A";
            const time = frame.current * 0.02;

            // Draw cloud puffs
            for (let i = 0; i < 5; i++) {
              const x = w * 0.2 + i * w * 0.15 + Math.sin(time + i) * 2;
              const y = h * 0.4 + Math.cos(time + i) * 3;
              const size = 10 + i * 2;

              ctx.beginPath();
              ctx.arc(x, y, size, 0, Math.PI * 2);
              ctx.fill();
            }

            // Lightning flash
            if (Math.sin(time * 3) > 0.9) {
              ctx.strokeStyle = "#FFEE58";
              ctx.lineWidth = 2;
              ctx.beginPath();

              const startX = w * 0.5;
              const startY = h * 0.5;

              ctx.moveTo(startX, startY);

              let x = startX;
              let y = startY;

              for (let i = 0; i < 3; i++) {
                x += (Math.random() - 0.5) * 20;
                y += 10;
                ctx.lineTo(x, y);
              }

              ctx.stroke();
            }

            // Rain drops
            ctx.fillStyle = "rgba(144,202,249,0.7)";
            for (let i = 0; i < 20; i++) {
              const x = ((i * w) / 20 + time * 50) % w;
              const y = (i * 17 + time * 100) % h;

              ctx.beginPath();
              ctx.ellipse(x, y, 1, 3, 0, 0, Math.PI * 2);
              ctx.fill();
            }
          }
          break;

        // Tier-2 combos
        case "dustDrift":
          {
            // very gentle drifting dust
            ctx.fillStyle = e.color || "#BCAAA4";
            ctx.fillRect(0, 0, w, h);
            ctx.fillStyle = "rgba(200,200,200,0.3)";
            const t = frame.current * 0.02;
            for (let i = 0; i < 20; i++) {
              const x = ((i * w) / 20 + Math.sin(t + i) * 5) % w;
              const y = ((i * h) / 20 + t * 5) % h;
              ctx.beginPath();
              ctx.arc(x, y, 2, 0, Math.PI * 2);
              ctx.fill();
            }
          }
          break;

        case "rustSpread":
          {
            // creeping rust spots
            ctx.fillStyle = e.color || "#D84315";
            ctx.fillRect(0, 0, w, h);
            ctx.fillStyle = "rgba(150,50,20,0.4)";
            const time = frame.current * 0.01;
            for (let i = 0; i < 10; i++) {
              const angle = time + i * ((Math.PI * 2) / 10);
              const radius = (w / 2) * (0.5 + 0.5 * Math.sin(time + i));
              const x = w / 2 + Math.cos(angle) * radius;
              const y = h / 2 + Math.sin(angle) * radius;
              ctx.beginPath();
              ctx.arc(x, y, 4, 0, Math.PI * 2);
              ctx.fill();
            }
          }
          break;

          case "sparkZap": {
            // quick zappy sparks
            ctx.fillStyle = e.color || "#FFF176";
            ctx.fillRect(0, 0, w, h);
            if (frame.current % 20 < 2) {
              ctx.strokeStyle = "white";
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.moveTo(w / 2, 0);
              ctx.lineTo(w / 2, h);
              ctx.stroke();
            }
          }
          break;

        case "clayForm":
          {
            // pulsing clay mound
            ctx.fillStyle = e.color || "#D7CCC8";
            ctx.fillRect(0, 0, w, h);
            ctx.fillStyle = "rgba(120,80,50,0.5)";
            const pulse = Math.abs(Math.sin(frame.current * 0.05)) * (w * 0.3);
            ctx.beginPath();
            ctx.ellipse(
              w / 2,
              h / 2,
              w * 0.3 + pulse,
              h * 0.2 + pulse * 0.5,
              0,
              0,
              Math.PI * 2
            );
            ctx.fill();
          }
          break;

        case "glassShimmer":
          {
            // shifting glass highlights
            ctx.fillStyle = e.color || "#B2EBF2";
            ctx.fillRect(0, 0, w, h);
            const offset = (frame.current * 2) % w;
            ctx.fillStyle = "rgba(255,255,255,0.3)";
            ctx.fillRect(offset - w, 0, w * 0.5, h);
            ctx.fillRect(offset, 0, w * 0.5, h);
          }
          break;

        case "magmaBubble":
          {
            // rising magma bubbles
            ctx.fillStyle = e.color || "#FF5722";
            ctx.fillRect(0, 0, w, h);
            ctx.fillStyle = "rgba(255,200,100,0.4)";
            const t2 = frame.current * 0.03;
            for (let i = 0; i < 5; i++) {
              const x = w * 0.2 + i * (w * 0.15);
              const y = h - ((t2 + i) % 1) * h;
              ctx.beginPath();
              ctx.arc(x, y, 5 + Math.sin(t2 + i) * 2, 0, Math.PI * 2);
              ctx.fill();
            }
          }
          break;

        // Tier-3…
        case "geyserBlast":
          {
            // pulse radial spray
            ctx.fillStyle = e.color || "#81D4FA";
            ctx.fillRect(0, 0, w, h);
            const radius = (Math.sin(frame.current * 0.1) * 0.3 + 0.7) * w;
            ctx.fillStyle = "rgba(255,255,255,0.5)";
            ctx.beginPath();
            ctx.arc(w / 2, h / 2, radius, 0, Math.PI * 2);
            ctx.fill();
          }
          break;

        case "tsunamiWave":
          {
            // rolling wave stripes
            ctx.fillStyle = e.color || "#0288D1";
            ctx.fillRect(0, 0, w, h);
            ctx.strokeStyle = "rgba(255,255,255,0.4)";
            ctx.lineWidth = 3;
            const speed = frame.current * 0.05;
            for (let y = 0; y < h; y += 20) {
              ctx.beginPath();
              for (let x = 0; x <= w; x += 10) {
                const dy = Math.sin(x * 0.1 + speed) * 10;
                ctx.lineTo(x, y + dy);
              }
              ctx.stroke();
            }
          }
          break;

        // Tier-4…
        case "treeGrow":
          {
            // simple growing trunk
            ctx.fillStyle = e.color || "#33691E";
            ctx.fillRect(0, 0, w, h);
            ctx.strokeStyle = "rgba(0,0,0,0.3)";
            const t3 = (Math.sin(frame.current * 0.03) + 1) / 2;
            ctx.lineWidth = 4;
            ctx.beginPath();
            ctx.moveTo(w / 2, h);
            ctx.lineTo(w / 2, h - t3 * h);
            ctx.stroke();
          }
          break;

        case "rainFall":
          {
            // falling raindrops
            ctx.fillStyle = e.color || "#90CAF9";
            ctx.fillRect(0, 0, w, h);
            ctx.fillStyle = "rgba(200,200,255,0.5)";
            const t4 = frame.current * 0.1;
            for (let i = 0; i < 10; i++) {
              const x = ((i * w) / 10 + t4 * 30) % w;
              const y = ((i * h) / 10 + t4 * 50) % h;
              ctx.beginPath();
              ctx.ellipse(x, y, 2, 6, 0, 0, Math.PI * 2);
              ctx.fill();
            }
          }
          break;

  
        case "cityBuild":
          {
            // blinking city lights
            ctx.fillStyle = e.color || "#78909C";
            ctx.fillRect(0, 0, w, h);
            if (frame.current % 30 < 10) ctx.fillStyle = "yellow";
            else ctx.fillStyle = "rgba(255,255,0,0.3)";
            for (let i = 0; i < 5; i++) {
              ctx.fillRect(i * (w / 5) + 10, h * 0.6, w / 10, h * 0.4);
            }
          }
          break;

        // Tier-6…
        case "rocketLaunch":
          {
            // rocket trail
            ctx.fillStyle = e.color || "#B0BEC5";
            ctx.fillRect(0, 0, w, h);
            const flame = Math.abs(Math.sin(frame.current * 0.2)) * w * 0.3;
            ctx.fillStyle = "orange";
            ctx.beginPath();
            ctx.moveTo(w / 2, h);
            ctx.lineTo(w / 2 - 10, h + flame);
            ctx.lineTo(w / 2 + 10, h + flame);
            ctx.fill();
          }
          break;

        case "aiThink":
          {
            // pulsing neural glow
            const glow = Math.sin(frame.current * 0.04) * 0.2 + 0.8;
            const grad = ctx.createRadialGradient(
              w / 2,
              h / 2,
              0,
              w / 2,
              h / 2,
              (w / 2) * glow
            );
            grad.addColorStop(0, "#FFF");
            grad.addColorStop(1, e.color || "#5C6BC0");
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, w, h);
          }
          break;

        case "cyborgMove":
          {
            // flickering cyber overlay
            ctx.fillStyle = e.color || "#90A4AE";
            ctx.fillRect(0, 0, w, h);
            if (frame.current % 20 < 2) {
              ctx.fillStyle = "rgba(0,255,0,0.2)";
              ctx.fillRect(0, 0, w, h);
            }
          }
          break;

        case "timeMachineWarp":
          {
            // radial warp rings
            ctx.fillStyle = e.color || "#9575CD";
            ctx.fillRect(0, 0, w, h);
            ctx.strokeStyle = "white";
            const rings = 3;
            for (let i = 0; i < rings; i++) {
              const r = (frame.current * (0.5 + i / rings)) % (w / 2);
              ctx.beginPath();
              ctx.arc(w / 2, h / 2, r, 0, Math.PI * 2);
              ctx.stroke();
            }
          }
          break;

        // Default - handle other animations with a simple display
        default: {
          // Base color fill with subtle animation
          const color = e.color || "#999";
          ctx.fillStyle = color;
          ctx.fillRect(0, 0, w, h);

          // Add subtle animation based on element tier
          if (e.tier) {
            // Higher tier elements get more complex animations
            const time = frame.current * 0.03;

            // Add a subtle pulsing effect
            const pulseSize = Math.sin(time) * 0.1 + 0.9;
            const grad = ctx.createRadialGradient(
              w / 2,
              h / 2,
              w * 0.1,
              w / 2,
              h / 2,
              (w / 2) * pulseSize
            );

            // Create a slightly lighter version of the base color for the gradient
            ctx.fillStyle = grad;
            grad.addColorStop(0, color);
            grad.addColorStop(1, "rgba(255,255,255,0.1)");

            ctx.beginPath();
            ctx.arc(w / 2, h / 2, w / 2, 0, Math.PI * 2);
            ctx.fill();

            // Add subtle particles for higher tier elements
            if (e.tier > 2) {
              ctx.fillStyle = "rgba(255,255,255,0.4)";
              for (let i = 0; i < e.tier; i++) {
                const angle = time + (i * Math.PI * 2) / e.tier;
                const dist = w * 0.3 * (0.8 + Math.sin(time * 2 + i) * 0.2);
                const x = w / 2 + Math.cos(angle) * dist;
                const y = h / 2 + Math.sin(angle) * dist;

                ctx.beginPath();
                ctx.arc(x, y, 2, 0, Math.PI * 2);
                ctx.fill();
              }
            }
          }
        }
      }

      ctx.restore();

      // Draw border
      ctx.strokeStyle = "#333";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, w / 2 - 1, 0, Math.PI * 2);
      ctx.stroke();

      animationId = requestAnimationFrame(draw);
    }

    animationId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animationId);
  }, [e, size]);

  return <canvas ref={canvasRef} width={size} height={size} />;
}
